import { Link } from "react-router-dom";
import { Wrench, ClipboardCheck, Hammer, Gauge, Snowflake, Cog, Disc, BatteryCharging, ScanLine, Package, CalendarClock, ArrowUpRight } from "lucide-react";
import { MaskLine, Reveal, ClipImage } from "../components/Motion";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ICONS = [Wrench, ClipboardCheck, Cog, ScanLine, Disc, Snowflake, Gauge, BatteryCharging, Hammer, Package];
const ITEMS_DEFAULT = [
  { t: "Onderhoud & beurten", d: "Klein en groot onderhoud voor alle merken, met de modernste apparatuur en behoud van fabrieksgarantie." },
  { t: "APK-keuring", d: "Snel en betrouwbaar gekeurd. Klein onderhoud combineren we graag met de APK." },
  { t: "Reparaties", d: "Van distributieriem tot koppeling — vakkundig reparatiewerk, eerlijk geadviseerd." },
  { t: "Diagnose & storingen", d: "Foutcodes uitlezen en storingen opsporen met professionele diagnoseapparatuur." },
  { t: "Remmen & uitlaat", d: "Controle en vervanging van remblokken, -schijven en uitlaatsystemen." },
  { t: "Airco-service", d: "Aircocheck, bijvullen en reinigen zodat u het hele jaar comfortabel rijdt." },
  { t: "Bandenservice & opslag", d: "Wisselen, balanceren, uitlijnen en seizoensopslag van uw banden." },
  { t: "Accu & elektronica", d: "Accutest en vervanging, plus reparatie van auto-elektronica." },
  { t: "Schadeherstel", d: "Erkend schadegarant reparateur — schade snel en vakkundig hersteld." },
  { t: "Ford onderdelen & accessoires", d: "Originele Ford-onderdelen en accessoires, direct gemonteerd." },
];

export default function Werkplaats() {
  const { get } = useContent();
  const services = get("werkplaats.diensten.items", ITEMS_DEFAULT);
  return (
    <div className="pb-24 min-h-screen">
      {/* HERO */}
      <section className="relative pt-32 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary mb-4 flex items-center gap-2">
              <Wrench size={15} /> {get("werkplaats.hero.eyebrow", "Erkend Ford-reparateur")}
            </p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-text-main">
              <MaskLine>{get("werkplaats.hero.title1", "Onze")}</MaskLine>
              <MaskLine delay={0.12}><span className="text-primary">{get("werkplaats.hero.title2", "werkplaats")}</span></MaskLine>
            </h1>
            <p className="mt-6 text-lg text-text-muted max-w-md leading-relaxed">
              {get("werkplaats.hero.intro", "Onderhoud, APK, reparatie en schadeherstel — allemaal in eigen huis, door deskundige monteurs met moderne apparatuur. Voor élk merk, met een eerlijk advies.")}
            </p>
            <Link to="/afspraak" data-testid="werkplaats-afspraak-cta"
              className="mt-8 group inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-4 hover:bg-primary-light transition-colors">
              <CalendarClock size={18} /> {get("werkplaats.hero.cta", "Direct online afspraak maken")}
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
          <ClipImage src={fileUrl(get("afbeeldingen.werkplaats_hero", HERO))} alt="Monteur aan het werk in de werkplaats" className="aspect-[4/3]" />
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-primary">{get("werkplaats.diensten.eyebrow", "Diensten")}</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">{get("werkplaats.diensten.title", "Alles voor uw auto onder één dak")}</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {services.map((s, i) => {
            const Icon = ICONS[i] || Wrench;
            return (
              <Reveal key={i} delay={(i % 3) * 0.06} className="bg-background p-8 hover:bg-surface transition-colors">
                <Icon size={28} className="text-primary" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-lg mt-5">{s.t}</h3>
                <p className="text-text-muted text-sm mt-2 leading-relaxed">{s.d}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12">
        <Reveal>
          <div className="relative overflow-hidden bg-primary text-white grain px-8 sm:px-16 py-16 text-center">
            <div className="relative z-10">
              <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight uppercase max-w-2xl mx-auto leading-[0.95]">
                {get("werkplaats.cta.title", "Auto toe aan onderhoud of APK?")}
              </h2>
              <p className="mt-5 text-white/70 max-w-xl mx-auto">{get("werkplaats.cta.text", "Plan online een afspraak of bel ons — wij helpen u graag verder.")}</p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/afspraak" className="inline-flex items-center gap-2 rounded-full bg-accent text-primary font-bold px-8 py-4 hover:bg-white transition-colors">
                  {get("werkplaats.cta.btn1", "Afspraak maken")}
                </Link>
                <a href="tel:0297261333" className="inline-flex items-center gap-2 rounded-full border border-white/30 font-semibold px-8 py-4 hover:bg-white/10 transition-colors">
                  {get("werkplaats.cta.btn2", "Bel 0297 - 261 333")}
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
